/**
 * Homepage Template
 *
 * Template for rendering the homepage from Prismic.
 * Uses SliceZone to render the page body (slices).
 */

import React from "react";
import { graphql } from "gatsby";
import { withPrismicPreview } from "gatsby-plugin-prismic-previews";
import { SliceZone } from "@prismicio/react";

import Layout from "../components/Layout";
import Seo from "../components/Seo";
import { components } from "../slices";

const HomepageTemplate = ({ data, pageContext }) => {
  if (!data) return null;

  const homepage = data.prismicHomepage || {};
  const topMenu = data.prismicTopMenu?.data || {};
  const footerData = data.prismicFooter?.data || {};

  return (
    <Layout topMenu={topMenu} footerData={footerData} slug={pageContext.slug}>
      <SliceZone slices={homepage.data?.body || []} components={components} />
    </Layout>
  );
};

/**
 * GraphQL Query
 *
 * Fetches the homepage document and global components (TopMenu, Footer).
 * Uses fragments from slice and component files.
 */
export const query = graphql`
  query HomepageQuery($id: String!) {
    prismicHomepage(id: { eq: $id }) {
      _previewable
      data {
        meta_title
        meta_description
        meta_image {
          url
        }
        body {
          ... on PrismicSlice {
            id
            slice_type
            slice_label
          }
          ...HomepageDataBodyHero
          ...HomepageDataBodyIntro
          ...HomepageDataBodyAbout
          ...HomepageDataBodyBrands
          ...HomepageDataBodyFeaturedVideo
          ...HomepageDataBodyPartners
          ...HomepageDataBodySocialVideos
          ...HomepageDataBodyContact
          ...HomepageDataBodyShowcase
        }
      }
    }
    prismicTopMenu {
      ...TopMenuFragment
    }
    prismicFooter {
      ...FooterFragment
    }
  }
`;

/**
 * Head Component (Gatsby Head API)
 *
 * Handles SEO meta tags for the page.
 */
/**
 * Per-page SEO defaults. These are used when the Prismic editor leaves
 * the corresponding SEO field blank, so every page ships with a unique
 * description out of the box. Editors can still override any of these
 * from the SEO tab in Prismic.
 */
const HOMEPAGE_DEFAULT_DESCRIPTION =
  "Low Battery is the home for next-generation superfans in music & culture — a media network reaching 50M+ fans across rap, hip-hop, pop, gaming, and live culture.";

export const Head = ({ data, location }) => {
  const homepage = data?.prismicHomepage?.data || {};

  return (
    <Seo
      title={homepage.meta_title}
      description={homepage.meta_description || HOMEPAGE_DEFAULT_DESCRIPTION}
      image={homepage.meta_image?.url}
      pathname={location?.pathname}
    />
  );
};

export default withPrismicPreview(HomepageTemplate);
